/**
 * Plein écran natif et mini-fenêtre (Picture-in-Picture) d'une balise <video>, partagés par le
 * player live (HLS) et le player evergreen (mp4). Les fallbacks WebKit sont indispensables : sur
 * iPhone ni `requestFullscreen` ni l'API PiP standard n'existent.
 */

/** Variantes WebKit (iOS/Safari) de l'API Picture-in-Picture, absentes des types standard. */
type VideoWithWebkitPip = HTMLVideoElement & {
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
};

/** Plein écran natif iPhone : `requestFullscreen` n'existe pas, seul `webkitEnterFullscreen` marche. */
type VideoWithWebkitFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

/** iPad et Safari macOS antérieur à 16.4 n'exposent la Fullscreen API des éléments que préfixée. */
type ElementWithWebkitFullscreen = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

/** Apple device */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

/** Spectateur sur mobile : le PiP (bouton + auto au changement d'app) ne cible pas le desktop. */
export function isMobileViewer(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (isAppleMobile()) return true;
  return /Android.+Mobile|Mobi|iPhone|iPod/i.test(navigator.userAgent);
}

export function isVideoPlaying(video: HTMLVideoElement): boolean {
  return !video.paused && !video.ended && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
}

/**
 * Un élément quelconque peut-il passer en plein écran ? Sur iPhone la Fullscreen API n'existe que
 * sur `<video>` : y basculer rouvrirait les contrôles natifs (barre de progression comprise), que
 * `controls={false}` ne peut pas masquer. Les appelants qui doivent garder la main sur l'avancement
 * masquent donc leur bouton plein écran quand cette capacité manque.
 */
export function supportsElementFullscreen(element: HTMLElement | null | undefined): boolean {
  if (!element) return false;
  if (typeof element.requestFullscreen === 'function') return true;
  return typeof (element as ElementWithWebkitFullscreen).webkitRequestFullscreen === 'function';
}

/**
 * Passe la vidéo en plein écran natif. C'est ce qui rend la vidéo éligible à l'auto-PiP
 * au changement d'app (iOS comme Chrome ne miniaturisent que le média « actif »).
 *
 * `container` permet de passer le cadre plutôt que la balise `<video>` : Safari macOS réaffiche ses
 * contrôles natifs (barre de progression comprise) dès qu'une vidéo passe en plein écran, alors
 * qu'un élément quelconque en plein écran n'en a aucun. L'iPhone ne connaît la Fullscreen API sous
 * aucune forme hors `<video>` : on y retombe sur le plein écran vidéo natif, contrôles inclus.
 */
export function requestVideoFullscreen(
  video: HTMLVideoElement,
  container?: HTMLElement | null,
): void {
  const webkit = video as VideoWithWebkitFullscreen;
  const webkitContainer = container as ElementWithWebkitFullscreen | null | undefined;
  try {
    if (container && typeof container.requestFullscreen === 'function') {
      void container.requestFullscreen();
    } else if (webkitContainer && typeof webkitContainer.webkitRequestFullscreen === 'function') {
      // iPad et vieux Safari macOS : sans ce repli on basculerait sur le plein écran de la balise,
      // qui rouvre les contrôles natifs.
      webkitContainer.webkitRequestFullscreen();
    } else if (typeof video.requestFullscreen === 'function') {
      void video.requestFullscreen();
    } else if (typeof webkit.webkitEnterFullscreen === 'function') {
      webkit.webkitEnterFullscreen();
    }
  } catch {
    /* ignore */
  }
}

/**
 * Élément actuellement en plein écran, quelle que soit la forme de l'API. Indispensable au repli
 * préfixé : `document.fullscreenElement` reste vide sur iPad et vieux Safari macOS, ce qui ferait
 * croire à l'appelant qu'aucun plein écran n'est en cours (bouton de sortie inopérant).
 */
export function getFullscreenElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/** Sort du plein écran (standard, préfixé, ou plein écran vidéo natif iPhone). */
export function exitVideoFullscreen(video: HTMLVideoElement): void {
  const webkit = video as VideoWithWebkitFullscreen;
  const doc =
    typeof document !== 'undefined'
      ? (document as Document & { webkitExitFullscreen?: () => void })
      : null;
  try {
    if (getFullscreenElement()) {
      if (typeof document !== 'undefined' && typeof document.exitFullscreen === 'function') {
        void document.exitFullscreen();
      } else if (doc && typeof doc.webkitExitFullscreen === 'function') {
        doc.webkitExitFullscreen();
      }
    } else if (
      webkit.webkitDisplayingFullscreen &&
      typeof webkit.webkitExitFullscreen === 'function'
    ) {
      webkit.webkitExitFullscreen();
    }
  } catch {
    /* ignore */
  }
}

export function isInPictureInPicture(video: HTMLVideoElement): boolean {
  if (typeof document !== 'undefined' && document.pictureInPictureElement === video) return true;
  return (video as VideoWithWebkitPip).webkitPresentationMode === 'picture-in-picture';
}

/** Le device sait-il afficher une mini-fenêtre pour cette vidéo (sinon on masque le bouton). */
export function supportsPictureInPicture(video: HTMLVideoElement): boolean {
  if (
    typeof document !== 'undefined' &&
    document.pictureInPictureEnabled &&
    !video.disablePictureInPicture
  ) {
    return true;
  }
  return (
    (video as VideoWithWebkitPip).webkitSupportsPresentationMode?.('picture-in-picture') === true
  );
}

/** Passe la vidéo en mini-fenêtre si le contexte le permet ; silencieux sinon (geste requis, non supporté). */
export async function enterPictureInPicture(video: HTMLVideoElement): Promise<void> {
  if (typeof document === 'undefined' || isInPictureInPicture(video)) return;
  const webkit = video as VideoWithWebkitPip;
  try {
    if (
      document.pictureInPictureEnabled &&
      !video.disablePictureInPicture &&
      typeof video.requestPictureInPicture === 'function'
    ) {
      await video.requestPictureInPicture();
    } else if (
      typeof webkit.webkitSetPresentationMode === 'function' &&
      webkit.webkitSupportsPresentationMode?.('picture-in-picture')
    ) {
      webkit.webkitSetPresentationMode('picture-in-picture');
    }
  } catch {
    /* refus du navigateur (pas de geste utilisateur, contexte non compatible) : on ne force rien */
  }
}

/** Sort de la mini-fenêtre pour réafficher la vidéo en grand au retour sur Welya. */
export async function exitPictureInPicture(video: HTMLVideoElement): Promise<void> {
  if (typeof document === 'undefined') return;
  const webkit = video as VideoWithWebkitPip;
  try {
    if (
      document.pictureInPictureElement === video &&
      typeof document.exitPictureInPicture === 'function'
    ) {
      await document.exitPictureInPicture();
    } else if (
      webkit.webkitPresentationMode === 'picture-in-picture' &&
      typeof webkit.webkitSetPresentationMode === 'function'
    ) {
      webkit.webkitSetPresentationMode('inline');
    }
  } catch {
    /* ignore */
  }
}
